import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { policyTypes as pTypes } from '@ourbranch/policy-types';
import { addDays, parseISO } from 'date-fns';

import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import { CollapsibleTable } from 'core/components/sortable-table';
import { dateFormatter, getTotalPrice, numberFormatter } from 'core/helpers/formatters';
import { get, getUmbrellaSuffix, policyTypes } from 'core/helpers/lookups';
import { useStore } from 'core/store';
import { getPolicyStatus, getPolicyStatusLabel, PolicyStatus, isImportedPolicy } from 'core/helpers/policy-status';
import { checkAdditionalCoverages } from 'core/helpers/quoter.service';
import useSession from 'core/hooks/use-session';
import { getPolicyProducer } from '../agent-details/agent-details-source';
import useStyles from './policies.styles';

const getPolicyType = get(policyTypes);

// `policy.endDate` is stored as the last covered day (`getPolicyEndDate` in
// packages/rater-helpers subtracts 1 ms before truncating to a date), so a
// 12-month policy effective 2026-05-05 has endDate 2027-05-04. The dec/app/
// quote render the policy period as the exclusive boundary — "Policy Period:
// 05/05/2026 to 05/05/2027". Add one day before formatting so the customer-
// page row matches the dec wording. (#30877)
//
// Exception: a flat cancellation stores endDate equal to effectiveDate (the
// cancellation form, the policy-mod preview clamp, and the automated-
// cancellation cron all enforce the equality, and it's how flatCancel is
// detected backend-side), so the policy was never in force — adding a day
// would fabricate coverage. Render the same-day range instead. (#31794)
export const formatPolicyEffectiveRange = (effectiveDate, endDate) => {
  if (!endDate) {
    return `${dateFormatter(effectiveDate)} - `;
  }
  const isFlatCancel = endDate === effectiveDate;
  const formattedEnd = dateFormatter(isFlatCancel ? parseISO(endDate) : addDays(parseISO(endDate), 1));
  return `${dateFormatter(effectiveDate)} - ${formattedEnd}`;
};

const Policies = observer(function Policies() {
  const classes = useStyles();
  const history = useHistory();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const { account } = useStore();
  const { isAgency } = useSession();
  const {
    id,
    policies: { list, loading: policiesLoading, getUnsignedBixConversion }
  } = account;
  const loading = policiesLoading || !list.length;

  const rowColor = ({ status, mode, id, policyType, policyDetails, offer }) => {
    if (status === PolicyStatus.InCancellation) {
      return 'red';
    }

    const bixConversionDocsInfo = getUnsignedBixConversion(id);
    const { canAddEarthquake, canAddFlood } = checkAdditionalCoverages(offer, policyType, isAgency);

    if (
      !bixConversionDocsInfo?.signedDocUploadedInS3 ||
      ((policyType === pTypes.HABundle || policyType === pTypes.Home) &&
        ((!isAgency &&
          policyDetails?.homeCoverage?.interestedInSeparateWindHail &&
          !policyDetails?.homeCoverage?.externalPolicyId) ||
          (canAddEarthquake && policyDetails?.includeEarthquake && !policyDetails?.earthquakeCoverage?.policyId) ||
          (canAddFlood && policyDetails?.includeFlood && !policyDetails?.floodCoverage?.policyId)))
    ) {
      return 'orange';
    }

    return mode;
  };

  const data = list.map((p) => {
    const status = getPolicyStatus(p);
    return {
      ...p,
      producer: getPolicyProducer(p) || 'N/A',
      totalPrice: getTotalPrice(p.premium, p.fees, p.surplusContribution || 0),
      status,
      statusLabel: getPolicyStatusLabel(p, status),
      rowMode: rowColor({
        status,
        mode: ['Inactive', 'Cancelled'].includes(status) ? 'light' : 'dark',
        id: p.id,
        policyType: p.policyType,
        policyDetails: p.policyDetails,
        offer: p.offer
      })
    };
  });

  const inactivePolicies = [];
  const activePolicies = [];
  const cancelledPolicies = [];

  data.forEach((row) => {
    if (row.status === 'Inactive') {
      inactivePolicies.push(row);
    } else if (row.status === 'Cancelled') {
      cancelledPolicies.push(row);
    } else {
      activePolicies.push(row);
    }
  });

  const sections = [
    {
      title: 'Active',
      rows: activePolicies,
      individual: true
    },
    ...(inactivePolicies.length
      ? [
          {
            title: 'Inactive Policies',
            rows: inactivePolicies,
            mode: 'light',
            expanded: inactiveOpen,
            setExpanded: setInactiveOpen
          }
        ]
      : []),
    ...(cancelledPolicies.length
      ? [
          {
            title: 'Cancelled Policies',
            rows: cancelledPolicies,
            mode: 'light',
            expanded: cancelOpen,
            setExpanded: setCancelOpen
          }
        ]
      : [])
  ];

  return (
    <Section title="Policies" rightLabel="Total" rightValue={`${loading ? '...' : list.length} policies`}>
      {loading ? (
        <Loading type="secondary" />
      ) : (
        <Card className={classes.tableContainer} type="secondary">
          <CollapsibleTable
            mode="dark"
            dataCy="policies"
            selfContained={false}
            headers={[
              ['policyType', 'Policy'],
              ['effectiveDate', 'Effective Dates'],
              ['totalPrice', 'Total Price'],
              ['producer', 'Producer'],
              ['status', 'Status']
            ]}
            data={sections}
            onClick={(policy) => history.push(`/customer/${id}/policy/${policy.id}`)}
            formatters={{
              policyType: (p, row) => (
                <>
                  {`${getPolicyType(p)}${getUmbrellaSuffix(row)}`}
                  <Label type="infoLabel" className={classes.policyLabel}>
                    {row.id}
                  </Label>
                  {isImportedPolicy(row) && row.importedData?.conversionMetadata?.policyNumber && (
                    <Label type="infoLabel" className={classes.policyLabel}>
                      {row.source === 'guidewire-conversion' ? 'GW' : 'IMT'} #
                      {row.importedData.conversionMetadata.policyNumber}
                    </Label>
                  )}
                </>
              ),
              status: (_status, row) => (
                // minWidth, not width: "Cancelled (rewritten)" (#36661) is wider than the plain labels.
                <Label className={classes.label} style={{ minWidth: '68px' }}>
                  {row.statusLabel}
                </Label>
              ),
              effectiveDate: (effectiveDate, row) => formatPolicyEffectiveRange(effectiveDate, row.endDate),

              coverage: (c) => (String(c).includes('/') ? c : numberFormatter(c)),
              totalPrice: (p) => `$${p.toFixed(2)}`
            }}
          />
        </Card>
      )}
    </Section>
  );
});

export default Policies;
